import { PublicClientApplication, InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser';

// Două metode de autentificare:
//  - cont Microsoft 365 (MSAL, token Entra ID)
//  - e-mail + parolă (token emis de serverul aplicației, păstrat local până expiră)
export interface PublicConfig { tenantId: string; clientId: string }
export interface AuthOptions { microsoft: boolean; password: boolean; requireMfa?: boolean }

let msal: PublicClientApplication;
let scopes: string[] = [];
let options: AuthOptions = { microsoft: true, password: false };

const LOCAL_KEY = 'moro.session';
interface LocalSession { token: string; expiresAt: number }

function localSession(): LocalSession | null {
  try {
    const s = JSON.parse(localStorage.getItem(LOCAL_KEY) ?? 'null') as LocalSession | null;
    if (s && s.expiresAt * 1000 > Date.now() + 30_000) return s;
  } catch {
    /* ignorat */
  }
  return null;
}

export async function initAuth(): Promise<PublicClientApplication> {
  const [cfg, opts] = await Promise.all([
    fetch('/api/public-config').then((r) => {
      if (!r.ok) throw new Error('Serverul nu răspunde');
      return r.json() as Promise<PublicConfig>;
    }),
    fetch('/api/auth/options').then((r) => (r.ok ? r.json() : options)).catch(() => options),
  ]);
  options = opts;
  msal = new PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: { cacheLocation: 'localStorage' },
  });
  scopes = [`api://${cfg.clientId}/access_as_user`];
  await msal.initialize();
  const result = await msal.handleRedirectPromise();
  if (result?.account) msal.setActiveAccount(result.account);
  else if (!msal.getActiveAccount() && msal.getAllAccounts().length) msal.setActiveAccount(msal.getAllAccounts()[0]);
  return msal;
}

export const authOptions = () => options;

// Cerere explicită de autentificare multifactor (MFA) către Microsoft
const MFA_CLAIMS = JSON.stringify({ access_token: { amr: { essential: true, values: ['mfa'] } } });

export function loginMicrosoft() {
  return msal.loginRedirect({ scopes, prompt: 'select_account', ...(options.requireMfa ? { claims: MFA_CLAIMS } : {}) });
}

/** Serverul a cerut MFA: reautentificare Microsoft cu cod. */
export function reauthenticateWithMfa() {
  const acc = msal.getActiveAccount();
  return msal.acquireTokenRedirect({ scopes, claims: MFA_CLAIMS, ...(acc ? { account: acc } : {}) });
}

/** Login cu link de activare / resetare (setează sesiunea locală). */
export function storeLocalSession(token: string, expiresAt: number) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify({ token, expiresAt }));
}

/** Login cu e-mail și parolă — pasul 1. Serverul trimite un cod pe e-mail. */
export async function loginWithPassword(email: string, password: string): Promise<{ challenge: string; email: string }> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? 'Autentificare eșuată');
  return { challenge: data.challenge, email: data.email };
}

/** Pasul 2: codul primit pe e-mail. */
export async function verifyLoginCode(challenge: string, code: string): Promise<{ mustChangePassword: boolean }> {
  const res = await fetch('/api/auth/login/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ challenge, code }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? 'Cod greșit');
  localStorage.setItem(LOCAL_KEY, JSON.stringify({ token: data.token, expiresAt: data.expiresAt }));
  return { mustChangePassword: !!data.mustChangePassword };
}

export function logout() {
  const hadLocal = !!localStorage.getItem(LOCAL_KEY);
  localStorage.removeItem(LOCAL_KEY);
  if (msal.getActiveAccount()) return msal.logoutRedirect();
  if (hadLocal) window.location.assign('/');
}

/** Există o sesiune (Microsoft sau parolă)? */
export function isSignedIn(): boolean {
  return !!localSession() || !!msal.getActiveAccount();
}

export function account(): AccountInfo | null {
  return msal.getActiveAccount();
}

export async function getToken(): Promise<string> {
  const local = localSession();
  if (local) return local.token;
  const acc = account();
  if (!acc) {
    window.location.assign('/');
    throw new Error('Sesiunea a expirat');
  }
  try {
    const r = await msal.acquireTokenSilent({ scopes, account: acc });
    return r.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes, account: acc });
    }
    throw e;
  }
}
